import { supabase } from "@/integrations/supabase/client";

// === Ledningsdashboard KPI:er ===
export const getDashboardStats = async () => {
  const admin = supabase;

  const [
    allMembersRes,
    storesRes,
    recentMembersRes,
    cancelledRecentRes,
    benefitUsageRes,
    partnerUsageRes,
    upcomingEventsRes,
    registrationsRes,
  ] = await Promise.all([
    admin.from("members").select("id, status, store_id, created_at, member_since"),
    admin.from("stores").select("id, name"),
    admin.from("members").select("id").gte("created_at", new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString()),
    admin.from("members").select("id").not("cancelled_at", "is", null).gte("cancelled_at", new Date(Date.now() - 30 * 24 * 3600 * 1000).toISOString()),
    admin.from("benefit_logs").select("benefit_label, used_at"),
    admin.from("partner_usage_logs").select("partner_id"),
    admin.from("events").select("id, title, starts_at, max_attendees, store_id").gte("starts_at", new Date().toISOString()).order("starts_at"),
    admin.from("event_registrations").select("event_id, status"),
  ]);

  const members = allMembersRes.data ?? [];
  const stores = storesRes.data ?? [];
  const active = members.filter((m) => m.status === "aktiv").length;
  const paying = members.filter((m) => ["aktiv", "vantar_betalning"].includes(m.status)).length;
  const cancelled30 = cancelledRecentRes.data?.length ?? 0;
  const totalMembers = members.length;
  const churnRate = totalMembers > 0 ? (cancelled30 / totalMembers) * 100 : 0;

  // Faktisk nettointäkt per medlem: 99 kr inkl. moms → 79,20 ex moms,
  // minus Stripe-avgift ≈ 75,91 kr netto per medlem/mån.
  const NET_PER_MEMBER = 75.91;
  const mrr = Math.round(active * NET_PER_MEMBER);
  const arr = mrr * 12;

  const membersByStore = stores.map((s) => ({
    store: s.name,
    members: members.filter((m) => m.store_id === s.id && m.status === "aktiv").length,
  }));

  const statusBreakdown = ["aktiv", "vantar_betalning", "obetald", "pausad", "uppsagd", "inaktiv"].map((status) => ({
    status,
    count: members.filter((m) => m.status === status).length,
  }));

  // Most/least used benefits
  const benefitMap = new Map<string, number>();
  (benefitUsageRes.data ?? []).forEach((b) => benefitMap.set(b.benefit_label, (benefitMap.get(b.benefit_label) ?? 0) + 1));
  const benefits = [...benefitMap.entries()].map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);

  // Partner usage
  const partnerMap = new Map<string, number>();
  (partnerUsageRes.data ?? []).forEach((p) => partnerMap.set(p.partner_id, (partnerMap.get(p.partner_id) ?? 0) + 1));
  const { data: partnerData } = await admin.from("partners").select("id, name");
  const topPartners = (partnerData ?? [])
    .map((p) => ({ name: p.name, count: partnerMap.get(p.id) ?? 0 }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  const regsByEvent = new Map<string, number>();
  (registrationsRes.data ?? []).forEach((r) => {
    if (r.status === "confirmed" || r.status === "checked_in") {
      regsByEvent.set(r.event_id, (regsByEvent.get(r.event_id) ?? 0) + 1);
    }
  });

  const upcoming = (upcomingEventsRes.data ?? []).slice(0, 5).map((e) => ({
    id: e.id,
    title: e.title,
    starts_at: e.starts_at,
    max_attendees: e.max_attendees,
    store: stores.find((s) => s.id === e.store_id)?.name ?? "—",
    registered: regsByEvent.get(e.id) ?? 0,
  }));

  return {
    kpi: {
      activeMembers: active,
      payingMembers: paying,
      totalMembers,
      newLast30: recentMembersRes.data?.length ?? 0,
      cancelledLast30: cancelled30,
      churnRate: Math.round(churnRate * 10) / 10,
      mrr,
      arr,
    },
    membersByStore,
    statusBreakdown,
    topBenefits: benefits.slice(0, 5),
    leastBenefits: benefits.slice(-3).reverse(),
    topPartners,
    upcoming,
  };
};
// === Medlemmar (kundregister) ===
export const listMembers = async () => {
  const { data: members } = await supabase
    .from("members")
    .select("id, member_number, first_name, last_name, email, phone, status, operator, phone_brand, phone_model, member_since, points, store_id")
    .order("created_at", { ascending: false });
  const { data: stores } = await supabase.from("stores").select("id, name");
  const storeMap = new Map((stores ?? []).map((s) => [s.id, s.name]));
  return (members ?? []).map((m) => ({ ...m, store: m.store_id ? storeMap.get(m.store_id) ?? "—" : "—" }));
};
export const getMemberDetails = async (data: { id: string }) => {
    const [{ data: member }, { data: invoices }, { data: benefits }, { data: registrations }] = await Promise.all([
      supabase.from("members").select("*, stores(name)").eq("id", data.id).maybeSingle(),
      supabase.from("invoices").select("*").eq("member_id", data.id).order("period", { ascending: false }).limit(12),
      supabase.from("benefit_logs").select("*").eq("member_id", data.id).order("used_at", { ascending: false }).limit(20),
      supabase.from("event_registrations").select("*, events(title, starts_at)").eq("member_id", data.id),
    ]);
    return { member, invoices: invoices ?? [], benefits: benefits ?? [], registrations: registrations ?? [] };
  };


// === Partners (för staff) ===
export const listPartnersWithStats = async () => {
  const [{ data: partners }, { data: usage }] = await Promise.all([
    supabase.from("partners").select("*").order("sort_order"),
    supabase.from("partner_usage_logs").select("partner_id"),
  ]);
  const map = new Map<string, number>();
  (usage ?? []).forEach((u) => map.set(u.partner_id, (map.get(u.partner_id) ?? 0) + 1));
  return (partners ?? []).map((p) => ({ ...p, usage_count: map.get(p.id) ?? 0 }));
};
// === Events (för staff) ===
export const listEventsWithStats = async () => {
  const [{ data: events }, { data: stores }, { data: regs }] = await Promise.all([
    supabase.from("events").select("*").order("starts_at"),
    supabase.from("stores").select("id, name"),
    supabase.from("event_registrations").select("event_id, status"),
  ]);
  const storeMap = new Map((stores ?? []).map((s) => [s.id, s.name]));
  const regMap = new Map<string, { confirmed: number; checked_in: number; waitlist: number }>();
  (regs ?? []).forEach((r) => {
    const cur = regMap.get(r.event_id) ?? { confirmed: 0, checked_in: 0, waitlist: 0 };
    if (r.status === "confirmed") cur.confirmed++;
    else if (r.status === "checked_in") cur.checked_in++;
    else if (r.status === "waitlist") cur.waitlist++;
    regMap.set(r.event_id, cur);
  });
  return (events ?? []).map((e) => ({
    ...e,
    store: e.store_id ? storeMap.get(e.store_id) ?? "—" : "—",
    stats: regMap.get(e.id) ?? { confirmed: 0, checked_in: 0, waitlist: 0 },
  }));
};